import './preparation.css';
import React, { useEffect } from 'react';
import { GlassCard } from '../ui/GlassCard';

interface JobWorkspaceShellProps {
  preparing?: boolean;
  header: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
}

export const JobWorkspaceShell: React.FC<JobWorkspaceShellProps> = ({ header, children, onClose, preparing=false }) => {
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    let focusFrame: number | undefined;

    const resetHorizontalPosition = () => {
      root.scrollLeft = 0;
      body.scrollLeft = 0;
      const workspace = document.querySelector<HTMLElement>('[data-testid="job-primary-workspace"]');
      if (workspace) workspace.scrollLeft = 0;
    };

    const keepWorkspaceTabsInView = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('.job-section-tabs')) return;

      // Overflow hidden still permits browser-initiated scrolling when a tab
      // receives focus. Restore only the inline position, preserving the
      // focused tab and the user's vertical position.
      resetHorizontalPosition();
      focusFrame = window.requestAnimationFrame(resetHorizontalPosition);
    };

    root.classList.add('job-workspace-open');
    body.classList.add('job-workspace-open');
    resetHorizontalPosition();
    document.addEventListener('focusin', keepWorkspaceTabsInView, true);

    return () => {
      document.removeEventListener('focusin', keepWorkspaceTabsInView, true);
      if (focusFrame !== undefined) window.cancelAnimationFrame(focusFrame);
      root.classList.remove('job-workspace-open');
      body.classList.remove('job-workspace-open');
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] flex min-w-0 items-center justify-center overflow-hidden bg-slate-950/95 backdrop-blur-3xl sm:p-3 2xl:p-6"
      onClick={onClose}
      data-testid="job-workspace-overlay" data-preparing={preparing}
    >
      <div className="h-full w-full min-w-0 max-w-full 2xl:h-[94vh] 2xl:max-w-[1800px] animate-fade-in-up" onClick={event => event.stopPropagation()} data-testid="job-workspace-frame">
        <GlassCard className="flex h-full w-full min-w-0 max-w-full flex-col overflow-hidden rounded-none border-white/10 bg-slate-900 p-0 shadow-3xl sm:rounded-3xl 2xl:rounded-[40px]">
          {header}
          <div className="flex min-h-0 w-full min-w-0 max-w-full flex-1 overflow-hidden" data-testid="job-workspace-body">
            {children}
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

interface JobPrimaryColumnsProps {
  workspace: React.ReactNode;
  quoteHistory: React.ReactNode;
}

export const JobPrimaryColumns: React.FC<JobPrimaryColumnsProps> = ({ workspace, quoteHistory }) => (
  <div className="flex-1 flex overflow-hidden min-w-0 min-h-0" data-testid="job-primary-columns">
    <main className="w-full min-w-0 max-w-full overflow-x-hidden overflow-y-auto px-4 py-5 sm:px-6 lg:px-10 lg:py-8 space-y-6 bg-slate-950/20 custom-scrollbar" data-testid="job-primary-workspace">
      {workspace}
    </main>
    {quoteHistory}
  </div>
);
